import React, { useState } from 'react'
import { Button, DatePicker, Select } from 'antd'
import dayjs from 'dayjs'
import { SlackOutlined } from '@ant-design/icons'
import {
  PendingPaymentReportAPI,
  PendingPaymentReportDateWiseAPI,
} from '../../AllAPI/AdminAPI'
import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import NotoFont from '../../Assets/Font/NotoSansDevanagari'
import axiosInstance from '../../Utils/axiosInstance'

const { Option } = Select

const PendingPaymentReport = () => {
  const selectedYear = Number(sessionStorage.getItem('year'))

  const [loading, setLoading] = useState(false)
  const [viewType, setViewType] = useState('excel')
  const [optionType, setOptionType] = useState('all')
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(dayjs())

  /* ---------------- Format Date ---------------- */
  const formatDate = (dateString) => {
    if (!dateString) return ''
    return dayjs(dateString).format('DD-MM-YYYY')
  }

  /* ---------------- Fetch Data ---------------- */
  const fetchOrders = async () => {
    if (!optionType || !selectedYear) return

    setLoading(true)

    try {
      let res

      if (optionType === 'all') {
        res = await axiosInstance.get(`${PendingPaymentReportAPI}/${selectedYear}`)
      }

      if (optionType === 'date') {
        if (!fromDate || !toDate) return

        res = await axiosInstance.post(PendingPaymentReportDateWiseAPI, {
          fromDate: fromDate.format('YYYY-MM-DD'),
          toDate: toDate.format('YYYY-MM-DD'),
        })
      }

      const result = Array.isArray(res?.data) ? res.data : []

      if (!result.length) {
        toast.info('No pending payment data found')
        return
      }

      if (viewType === 'excel') {
        generateExcel(result)
      } else {
        generatePDF(result)
      }
    } catch (err) {
      console.error('Pending report error:', err)
      toast.error('Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  /* ---------------- Excel ---------------- */
  // const generateExcel = (data) => {
  //   const formatted = data.map((item) => {
  //     const gross = Number(item.gross_total || 0)
  //     const loading = Number(item.loading_charges || 0)
  //     const discount = Number(item.discount_amount || 0)
  //     const netTotal = gross + loading - discount

  //     return {
  //       'Sales ID': item.sales_id,
  //       'Sales Date': formatDate(item.sales_date),
  //       'Customer Name': item.customer_name,
  //       Contact: item.contact,
  //       'Gross Amount': gross,
  //       'Loading Charges': loading,
  //       'Discount Amount': discount,
  //       'Net Total Amount': netTotal,
  //     }
  //   })

  //   const totalNet = formatted.reduce(
  //     (sum, row) => sum + row['Net Total Amount'],
  //     0,
  //   )

  //   formatted.push({
  //     'Sales ID': '',
  //     'Sales Date': '',
  //     'Customer Name': '',
  //     Contact: '',
  //     'Gross Amount': '',
  //     'Loading Charges': '',
  //     'Discount Amount': 'Grand Total',
  //     'Net Total Amount': totalNet,
  //   })

  //   const worksheet = XLSX.utils.json_to_sheet(formatted)
  //   const workbook = XLSX.utils.book_new()
  //   XLSX.utils.book_append_sheet(workbook, worksheet, 'Pending Payment Report')

  //   const excelBuffer = XLSX.write(workbook, {
  //     bookType: 'xlsx',
  //     type: 'array',
  //   })

  //   const blob = new Blob([excelBuffer], {
  //     type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  //   })

  //   const url = window.URL.createObjectURL(blob)
  //   window.open(url, '_blank')
  // }

  const generateExcel = (data, fileName = 'Pending_Payment_Report') => {
    const formatted = data.map((item) => {
      const gross = Number(item.gross_total || 0)
      const loading = Number(item.loading_charges || 0)
      const discount = Number(item.discount_amount || 0)
      const netTotal = gross + loading - discount

      return {
        'Sales ID': item.sales_id,
        'Sales Date': formatDate(item.sales_date),
        'Customer Name': item.customer_name,
        Contact: item.contact,
        'Gross Amount': gross,
        'Loading Charges': loading,
        'Discount Amount': discount,
        'Net Total Amount': netTotal,
      }
    })

    const totalNet = formatted.reduce(
      (sum, row) => sum + row['Net Total Amount'],
      0,
    )

    formatted.push({
      'Sales ID': '',
      'Sales Date': '',
      'Customer Name': '',
      Contact: '',
      'Gross Amount': '',
      'Loading Charges': '',
      'Discount Amount': 'Grand Total',
      'Net Total Amount': totalNet,
    })

    const worksheet = XLSX.utils.json_to_sheet(formatted)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Pending Payment Report')

    //  Generate Current Date (DD-MM-YYYY)
    const today = new Date()
    const formattedDate = `${String(today.getDate()).padStart(2, '0')}-${String(
      today.getMonth() + 1,
    ).padStart(2, '0')}-${today.getFullYear()}`

    const finalFileName = `${fileName} (${formattedDate}).xlsx`

    //  Direct Download
    XLSX.writeFile(workbook, finalFileName)
  }

  /* ---------------- PDF (AutoTable) ---------------- */
  const generatePDF = (data) => {
    //  Portrait A4
    const doc = new jsPDF('p', 'mm', 'a4')

    //  Marathi Font
    doc.addFileToVFS('NotoSansDevanagari.ttf', NotoFont)
    doc.addFont('NotoSansDevanagari.ttf', 'NotoSans', 'normal')
    doc.setFont('NotoSans')

    const tableColumns = [
      'Sales ID',
      'Sales Date',
      'Customer Name',
      'Contact',
      'Net Total Amount',
    ]

    const tableRows = data.map((item) => {
      const gross = Number(item.gross_total || 0)
      const loading = Number(item.loading_charges || 0)
      const discount = Number(item.discount_amount || 0)
      const netTotal = gross + loading - discount

      return [
        item.sales_id,
        formatDate(item.sales_date),
        item.customer_name,
        item.contact,
        netTotal,
      ]
    })

    const totalNetAmount = data.reduce((sum, item) => {
      const gross = Number(item.gross_total || 0)
      const loading = Number(item.loading_charges || 0)
      const discount = Number(item.discount_amount || 0)
      return sum + (gross + loading - discount)
    }, 0)

    //  Grand Total Row
    tableRows.push(['', '', '', 'Grand Total', totalNetAmount])

    //  Title (same style as RAW report)
    doc.setFontSize(14)
    doc.text('PENDING PAYMENT REPORT', 14, 15)

    const today = new Date().toLocaleDateString('en-GB')

    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      startY: 22,
      theme: 'grid',
      tableWidth: 'auto',

      styles: {
        font: 'NotoSans',
        fontSize: 8,
        cellPadding: 2,
      },

      headStyles: {
        fillColor: [255, 102, 0],
      },

      columnStyles: {
        4: { halign: 'right' }, //  Right align amount
      },

      margin: { left: 10, right: 10 },

      didDrawPage: function () {
        const pageCount = doc.internal.getNumberOfPages()
        const pageWidth = doc.internal.pageSize.getWidth()
        const pageHeight = doc.internal.pageSize.getHeight()

        doc.setFontSize(9)

        //  Page number (same position)
        doc.text(
          `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
          pageWidth - 45,
          pageHeight - 8,
        )

        //  Generated date (right aligned)
        doc.text(`(${today})`, pageWidth - 10, pageHeight - 8, {
          align: 'right',
        })
      },
    })

    const blob = doc.output('blob')
    window.open(URL.createObjectURL(blob), '_blank')
  }

  return (
    <div className="mainContainer">
      {loading && (
        <div className="fullscreen-loader">
          <div className="loader-box">
            <div className="spinner"></div>
            <p>Generating Report...</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="header-raw">
        <h3>
          <SlackOutlined style={{ color: 'orangered' }} /> Pending Payment
          Report
        </h3>
      </div>

      {/* Filters */}
      <div className="row p-2 m-0 mb-2 rounded border shadow">
        <div className="col-md-2 pt-2">
          <label>View Type</label>
          <Select
            style={{ width: '100%' }}
            value={viewType}
            onChange={(value) => {
              setViewType(value)
              setOptionType('all')
            }}
          >
            <Option value="excel">Excel</Option>
            <Option value="pdf">PDF</Option>
          </Select>
        </div>

        <div className="col-md-2 pt-2">
          <label>Options</label>
          <Select
            style={{ width: '100%' }}
            value={optionType}
            onChange={(value) => setOptionType(value)}
          >
            <Option value="all">All</Option>
            <Option value="date">Date Wise</Option>
          </Select>
        </div>

        {optionType === 'date' && (
          <>
            <div className="col-md-2 pt-2">
              <label>From Date</label>
              <DatePicker
                value={fromDate}
                onChange={setFromDate}
                format="DD-MM-YYYY"
              />
            </div>

            <div className="col-md-2 pt-2">
              <label>To Date</label>
              <DatePicker
                value={toDate}
                onChange={setToDate}
                format="DD-MM-YYYY"
              />
            </div>
          </>
        )}

        <div className="col-md-2 pt-2 d-flex align-items-end">
          <Button
            type="primary"
            className="rounded"
            loading={loading}
            disabled={
              !optionType ||
              (optionType === 'date' && (!fromDate || !toDate)) ||
              loading
            }
            onClick={fetchOrders}
          >
            Show
          </Button>
        </div>
      </div>
    </div>
  )
}

export default PendingPaymentReport
